import React from "react";
import { useTranslation } from "react-i18next";

import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm, Controller } from "react-hook-form";

import BackButton from "@/components/back-button";
import Button from "@/components/button";
import ReviewSubmitted from "@/components/review-submitted";
import Textarea from "@/components/textarea";
import { useAddBusinessReview } from "@/queries/hooks/ratingHooks";
import { reviewCategories } from "@/routes/app/profile/-categories";
import notify from "@/utils/notify";

import { makeProfileQuery } from "../-queries";

export const Route = createFileRoute(
  "/app/profile/employer/$employerId/review/",
)({
  component: EmployerReviewPage,
});

const StarRating = ({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (v: number) => void;
  label: string;
}) => {
  const { t } = useTranslation("profile");
  return (
    <div className="w-full flex flex-col">
      <div className="mb-1 body-small">{label}</div>
      <div className="flex flex-row justify-between h-[60px] bg-fog rounded-[5px] px-5 py-[10px] min-w-[300px] w-[300px] ">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            aria-label={t("review.rateStar", { count: star })}
            className="text-[20px] focus:outline-none flex flex-col"
            onClick={() => onChange(star)}
          >
            <span className="body-small">{star}</span>
            <span style={{ color: star <= value ? "#FBC468" : "#E5E5E5" }}>
              ★
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const categories = reviewCategories;

type FormValues = {
  rating: Record<string, number>;
  review_text: string;
  shift_id?: number;
};

export default function EmployerReviewPage() {
  const { t } = useTranslation("profile");
  const { employerId } = Route.useParams();
  const { data } = useSuspenseQuery(makeProfileQuery(employerId));
  const { business } = data;
  const [submitted, setSubmitted] = React.useState<boolean | null>(null);

  const mutation = useAddBusinessReview(Number(employerId));

  const defaultRating = React.useMemo(() => {
    return categories.reduce<Record<string, number>>((acc, cat) => {
      acc[cat.value] = 0;
      return acc;
    }, {});
  }, []);

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      rating: defaultRating,
      review_text: "",
      shift_id: 0,
    },
  });

  const onSubmit = (values: FormValues) => {
    const payload = {
      rating: values.rating,
      review_text: values.review_text,
      business_id: Number(employerId),
    };

    mutation.mutate(payload, {
      onSuccess: (responseData) => {
        setSubmitted(responseData.success);
      },
      onError: (error: any) => {
        if (error?.response?.data?.success === false) {
          notify.error(t("review.failed"), {
            description: t("review.alreadyReviewedEmployer"),
          });
        } else {
          notify.error(t("review.failed"), {
            description: error?.message || t("review.submitError"),
          });
        }
      },
    });
  };

  if (submitted !== null) {
    return <ReviewSubmitted name={business.name} success={submitted} />;
  }

  const employerName = business.name;

  return (
    <div className="flex flex-col gap-7 w-full max-w-[1140px]">
      <BackButton />
      <div className="w-full bg-white rounded-2xl shadow-md p-8 flex flex-col">
        <h2 className="heading-2 b-2">{t("review.rateExperience")}</h2>
        <div className="text-lg font-medium mb-6">{employerName}</div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-[55px]  max-w-[720px]">
            {categories.map((cat) => (
              <Controller
                key={cat.value}
                control={control}
                name={`rating.${cat.value}`}
                render={({ field }) => (
                  <StarRating
                    value={field.value}
                    onChange={(v) => field.onChange(v)}
                    label={cat.label}
                  />
                )}
              />
            ))}
          </div>
          <Controller
            name="review_text"
            control={control}
            render={({ field }) => (
              <Textarea
                label={t("review.writeOptional")}
                placeholder={t("review.writePlaceholder")}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                className="mb-8 mt-[45px] max-w-[720px]"
              />
            )}
          />
          <Button type="submit" className="w-[320px]" variant="secondary">
            {t("review.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
